import type { Decorator, FormApi } from 'final-form';
import { isBrowser } from '@/utils/environment';
import noop from '@gik/core/utils/noop';

// NOTE: very close to original implementation
// the only changed parts are:
// scrollToFirstError() - obtaining the invalid element and scroll instead of focus
// afterSubmit() - simplified condition
//
// original implementation
// https://github.com/final-form/final-form-focus/blob/HEAD/src/decorator.js

/**
 * Decorator to scroll to the first invalid field.
 *
 * @param invalidElementSelector selector for elements that have validation error
 */
const createDecorator = (invalidElementSelector = '.gik-form-group--error'): Decorator => (form: FormApi) => {
  const scrollToFirstError = () => {
    if (!isBrowser()) return;

    const firstInput = document.querySelector(invalidElementSelector) as HTMLElement;

    if (firstInput) {
      // small delay is needed for validation messages to show up
      // so scroll position can be correctly calculated
      setTimeout(() => {
        firstInput.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
      }, 1);
    }
  };
  // Save original submit function
  const originalSubmit = form.submit;

  // Subscribe to errors, and keep a local copy of them
  let state: { errors?: Object; submitErrors?: Object } = {};
  const unsubscribe = form.subscribe(
    nextState => {
      state = nextState;
    },
    { errors: true, submitErrors: true }
  );

  // What to do after submit
  const afterSubmit = () => {
    const { errors, submitErrors } = state;
    // has error check
    if ((errors && Object.keys(errors).length) || (submitErrors && Object.keys(submitErrors).length)) {
      scrollToFirstError();
    }
  };

  // Rewrite submit function
  form.submit = () => {
    const result = originalSubmit.call(form);
    if (result && typeof result.then === 'function') {
      // async
      result.then(afterSubmit, noop);
    } else {
      // sync
      afterSubmit();
    }
    return result;
  };

  return () => {
    unsubscribe();
    form.submit = originalSubmit;
  };
};

export default createDecorator;
